/**
 * F-197: video from other providers (Bunny Stream, Cloudflare Stream, Panda
 * Video and a plain HLS manifest), for course lessons and post embeds.
 *
 * Mirrors the API (`src/utils/externalVideo.js`): hosts are matched exactly or
 * by a fixed pattern, never by substring, and the player `src` is rebuilt from
 * the validated ids. The API is still the authority (it refuses the lesson and
 * sends the player in `videoEmbed` / `embed`); the app checks again before it
 * puts any of it in an iframe or a <video>.
 */

export type ExternalVideoProvider = 'bunny' | 'cloudflare' | 'panda' | 'hls'

/** The player the API sends: `videoEmbed` of a lesson, `embed` of a post link. */
export interface ExternalVideoEmbed {
  provider?: string
  kind: 'iframe' | 'hls'
  src: string
  /** Bunny or Cloudflare with the community's key: `src` carries a token until `expiresAt`. */
  signed?: boolean
  expiresAt?: string
  /** The community's key was refused: nothing plays until the team replaces it. */
  unavailable?: boolean
}

export interface ExternalVideoMatch {
  provider: ExternalVideoProvider
  /** False when it looks like the provider but cannot be played (wrong path, bad id, `http` HLS…). */
  valid: boolean
  embed?: { kind: 'iframe' | 'hls'; src: string }
}

const MAX_URL_LENGTH = 2048
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const BUNNY_LIBRARY = /^\d{1,10}$/
const CLOUDFLARE_UID = /^[0-9a-f]{32}$/i
const CLOUDFLARE_CUSTOMER_HOST = /^customer-([a-z0-9]{1,64})\.cloudflarestream\.com$/
const PANDA_HOST = /^[a-z0-9](?:[a-z0-9-]{0,78}[a-z0-9])?\.tv\.pandavideo\.com\.br$/
const INTERNAL_SUFFIX = /(^|\.)(localhost|local|internal|intranet|lan|home|corp|localdomain)\.?$/i

const isIpLiteral = (hostname: string) => hostname.startsWith('[') || /^[\d.]+$/.test(hostname) || /^0x[0-9a-f.x]+$/i.test(hostname)

const parse = (value: unknown): URL | null => {
  if (typeof value !== 'string') return null
  const text = value.trim()
  // eslint-disable-next-line no-control-regex
  if (!text || text.length > MAX_URL_LENGTH || /[\s\u0000-\u001f\u007f]/.test(text)) return null
  try {
    const url = new URL(text)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    if (url.username || url.password) return null
    return url
  } catch {
    return null
  }
}

const segments = (url: URL) => url.pathname.split('/').filter(Boolean)

const matchBunny = (url: URL): ExternalVideoMatch | null => {
  const host = url.hostname.toLowerCase()
  if (host !== 'iframe.mediadelivery.net' && host !== 'video.bunnycdn.com') return null
  const [kind, libraryId, videoId] = segments(url)
  const kinds = host === 'video.bunnycdn.com' ? ['play'] : ['embed', 'play']
  if (!kinds.includes(kind) || !BUNNY_LIBRARY.test(libraryId || '') || !UUID.test(videoId || '')) return { provider: 'bunny', valid: false }
  return { provider: 'bunny', valid: true, embed: { kind: 'iframe', src: `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId.toLowerCase()}` } }
}

const matchCloudflare = (url: URL): ExternalVideoMatch | null => {
  const host = url.hostname.toLowerCase()
  const customer = host.match(CLOUDFLARE_CUSTOMER_HOST)
  const generic = host === 'iframe.videodelivery.net' || host === 'watch.videodelivery.net'
  if (!customer && !generic) {
    return host.endsWith('.cloudflarestream.com') || host === 'cloudflarestream.com' ? { provider: 'cloudflare', valid: false } : null
  }
  const [uid] = segments(url)
  if (!CLOUDFLARE_UID.test(uid || '')) return { provider: 'cloudflare', valid: false }
  const id = uid.toLowerCase()
  const src = customer ? `https://customer-${customer[1]}.cloudflarestream.com/${id}/iframe` : `https://iframe.videodelivery.net/${id}`
  return { provider: 'cloudflare', valid: true, embed: { kind: 'iframe', src } }
}

const matchPanda = (url: URL): ExternalVideoMatch | null => {
  const host = url.hostname.toLowerCase()
  if (!host.endsWith('.tv.pandavideo.com.br')) return null
  const videoId = url.searchParams.get('v') || ''
  if (!PANDA_HOST.test(host) || !/^\/embed\/?$/.test(url.pathname) || !UUID.test(videoId)) return { provider: 'panda', valid: false }
  return { provider: 'panda', valid: true, embed: { kind: 'iframe', src: `https://${host}/embed/?v=${videoId.toLowerCase()}` } }
}

const matchHls = (url: URL): ExternalVideoMatch | null => {
  if (!/\.m3u8$/i.test(url.pathname)) return null
  const host = url.hostname.toLowerCase().replace(/\.$/, '')
  const valid = url.protocol === 'https:' && !url.port && host.includes('.') && !isIpLiteral(host) && !INTERNAL_SUFFIX.test(host)
  if (!valid) return { provider: 'hls', valid: false }
  const clean = new URL(url.toString())
  clean.hash = ''
  clean.hostname = host
  return { provider: 'hls', valid: true, embed: { kind: 'hls', src: clean.toString() } }
}

const MATCHERS = [matchBunny, matchCloudflare, matchPanda, matchHls]

/** The provider of a video URL among Bunny, Cloudflare, Panda and HLS, or null when it is none of them. */
export const matchExternalVideo = (value: unknown): ExternalVideoMatch | null => {
  const url = parse(value)
  if (!url) return null
  for (const matcher of MATCHERS) {
    const found = matcher(url)
    if (found) return found
  }
  return null
}

/* A signed Cloudflare player replaces the uid with a JWT (three base64url parts). */
const CLOUDFLARE_ID_OR_TOKEN = '(?:[0-9a-f]{32}|[A-Za-z0-9_-]{10,}\\.[A-Za-z0-9_-]{10,}\\.[A-Za-z0-9_-]{10,})'
const CLOUDFLARE_CUSTOMER_PATH = new RegExp(`^/${CLOUDFLARE_ID_OR_TOKEN}/iframe$`)
const CLOUDFLARE_GENERIC_PATH = new RegExp(`^/${CLOUDFLARE_ID_OR_TOKEN}$`)

/**
 * True when `src` is an iframe player of Bunny, Cloudflare or Panda that the
 * app agrees to load: the shapes the API builds, signed (Bunny's
 * `?token=…&expires=…`, Cloudflare's token in the path) or not.
 */
export const isExternalVideoIframeSrc = (src: unknown): src is string => {
  if (typeof src !== 'string' || src.length > MAX_URL_LENGTH) return false
  let url: URL
  try {
    url = new URL(src)
  } catch {
    return false
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.hash || url.port) return false
  const host = url.hostname
  if (host === 'iframe.mediadelivery.net') {
    const keys = [...url.searchParams.keys()].join(',')
    return /^\/embed\/\d{1,10}\/[0-9a-f-]{36}$/.test(url.pathname) && UUID.test(url.pathname.split('/')[3])
      && (!url.search || (keys === 'token,expires' && /^[a-f0-9]{64}$/.test(url.searchParams.get('token') || '') && /^\d{1,12}$/.test(url.searchParams.get('expires') || '')))
  }
  if (CLOUDFLARE_CUSTOMER_HOST.test(host)) return CLOUDFLARE_CUSTOMER_PATH.test(url.pathname) && !url.search
  if (host === 'iframe.videodelivery.net') return CLOUDFLARE_GENERIC_PATH.test(url.pathname) && !url.search
  if (PANDA_HOST.test(host)) return /^\/embed\/$/.test(url.pathname) && [...url.searchParams.keys()].join(',') === 'v' && UUID.test(url.searchParams.get('v') || '')
  return false
}

/** True when `src` is an HLS manifest the app plays in its own player (https, public host, `.m3u8`). */
export const isExternalHlsSrc = (src: unknown): src is string => {
  const found = matchExternalVideo(src)
  return Boolean(found && found.provider === 'hls' && found.valid && typeof src === 'string' && src.startsWith('https://'))
}

/** True when the player the API sent can be shown here (or is the "unavailable" notice). */
export const isPlayableExternalEmbed = (embed: Partial<ExternalVideoEmbed> | null | undefined): embed is ExternalVideoEmbed => {
  if (!embed) return false
  if (embed.unavailable) return true
  if (embed.kind === 'hls') return isExternalHlsSrc(embed.src)
  return embed.kind === 'iframe' && isExternalVideoIframeSrc(embed.src)
}
